import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SignupSchema } from "../../utils/zodSchema/user";
import SubHeader from "components/common/SubHeader";
import {
    postSignup,
    postSendVerificationCode,
    postCheckVerificationCode,
} from "../../api/user";

import "react-day-picker/dist/style.css";
import Blank from "components/common/Blank";
import CommonModal from "components/common/CommonModal";
import { trackSignUp } from "utils/analytics";

const SignupPage = () => {
    const navigate = useNavigate();
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(SignupSchema),
        mode: "onChange",
        defaultValues: {
            gender: "",
            birthDate: "",
        },
    });

    // 입력값 변경 감지
    const phone = watch("hp");
    const verificationCode = watch("verificationCode");
    const name = watch("name");
    const gender = watch("gender");

    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [isErrorMsg, setIsErrorMsg] = useState(null);
    const [isSendCode, setIsSendCode] = useState(false);
    const [isCodeConfirm, setIsCodeConfirm] = useState(false);
    const [policyAgree, setPolicyAgree] = useState({
        allAgree: false,
        termsAccepted: false,
        ageConfirmed: false,
        privacyAccepted: false,
        marketingConsent: {
            sms: false,
            email: false,
        },
    });

    const sendVerificationDebounceRef = useRef(null);

    // 인증번호 발송 (debounce 500ms)
    const handleSendVerification = () => {
        if (!phone) return;
        if (sendVerificationDebounceRef.current) {
            clearTimeout(sendVerificationDebounceRef.current);
        }
        sendVerificationDebounceRef.current = setTimeout(() => {
            postSendVerificationCode(phone)
                .then((res) => {
                    if (res.code === "0000") {
                        setIsSendCode(true);
                    }
                })
                .catch(() => {
                    console.error(
                        "인증번호 발송 중 오류가 발생했습니다. 다시 시도해주세요."
                    );
                });
        }, 500);
    };

    // 인증번호 일치 확인
    const handleVerifyCode = useCallback(async () => {
        if (!verificationCode || verificationCode.length !== 6) return;
        try {
            const res = await postCheckVerificationCode(
                phone,
                verificationCode
            );
            if (res.code === "0000") {
                setIsCodeConfirm(true);
            } else {
                setIsCodeConfirm(false);
            }
        } catch (error) {
            console.error("인증 확인 중 오류 발생:", error);
            setIsCodeConfirm(false);
        }
    }, [verificationCode, phone]);

    // 인증번호가 6자리가 되면 자동으로 확인
    useEffect(() => {
        if (verificationCode && verificationCode.length === 6 && isSendCode) {
            handleVerifyCode();
        }
    }, [verificationCode, isSendCode, handleVerifyCode]);

    const onChangePolicy = (e) => {
        const { name, checked } = e.target;
        if (name === "allAgree") {
            setPolicyAgree({
                ...policyAgree,
                allAgree: checked,
                termsAccepted: checked,
                ageConfirmed: checked,
                privacyAccepted: checked,
            });
        } else if (
            name === "termsAccepted" ||
            name === "ageConfirmed" ||
            name === "privacyAccepted"
        ) {
            const newPolicyAgree = { ...policyAgree, [name]: checked };
            // 필수 동의 항목들이 모두 체크되어 있는지 확인
            const isAllRequiredChecked =
                (name === "termsAccepted"
                    ? checked
                    : policyAgree.termsAccepted) &&
                (name === "ageConfirmed"
                    ? checked
                    : policyAgree.ageConfirmed) &&
                (name === "privacyAccepted"
                    ? checked
                    : policyAgree.privacyAccepted);

            setPolicyAgree({
                ...newPolicyAgree,
                allAgree: isAllRequiredChecked,
            });
        } else {
            setPolicyAgree({
                ...policyAgree,
                marketingConsent: {
                    ...policyAgree.marketingConsent,
                    [name]: checked,
                },
            });
        }
    };

    const activeSignupButton = () => {
        //allAgree가 true이면 모든 필수 항목에 체크되어 있다고 간주
        if (
            Object.keys(errors).length === 0 &&
            isCodeConfirm &&
            policyAgree.allAgree &&
            phone &&
            verificationCode &&
            name
        ) {
            return true;
        }
        return false;
    };

    const clickSignup = async (formData) => {
        try {
            postSignup(formData, policyAgree)
                .then((response) => {
                    if (response.code === "0000") {
                        // GA4: 회원가입 완료 이벤트 추적
                        trackSignUp("email");
                        setIsSuccessModalOpen(true);
                    }
                })
                .catch((error) => {
                    const errMsg = Object.values(error.response.data.detail);
                    setIsErrorMsg(errMsg);
                });
        } catch (error) {
            const errMsg = Object.values(error.response.data.detail);
            setIsErrorMsg(errMsg);
        }
    };

    // 생년월일 값 감지
    const birthDate = watch("birthDate");

    // 생년월일 자동 포맷팅
    useEffect(() => {
        if (birthDate) {
            let value = birthDate.replace(/[^0-9]/g, "");

            let formattedValue = "";
            if (value.length <= 4) {
                formattedValue = value;
            } else if (value.length <= 6) {
                formattedValue = value.slice(0, 4) + "-" + value.slice(4);
            } else if (value.length <= 8) {
                formattedValue =
                    value.slice(0, 4) +
                    "-" +
                    value.slice(4, 6) +
                    "-" +
                    value.slice(6, 8);
            } else {
                formattedValue =
                    value.slice(0, 4) +
                    "-" +
                    value.slice(4, 6) +
                    "-" +
                    value.slice(6, 8);
            }

            // 포맷팅된 값이 원래 값과 다를 때만 업데이트
            if (formattedValue !== birthDate) {
                setValue("birthDate", formattedValue);
            }
        }
    }, [birthDate, setValue]);

    const closeSuccessModal = () => {
        setIsSuccessModalOpen(false);
        navigate("/login", { replace: true }); // 메인 페이지로 이동 + 히스토리 안남기게 replace true로 설정
    };

    return (
        <>
            <SubHeader title="회원가입" />
            <div className="signup-container">
                <form
                    className="input-wrap"
                    onSubmit={handleSubmit(clickSignup)}
                >
                    <div className="input-form">
                        <input
                            placeholder="이메일"
                            className={`input ${errors.user_id ? "error" : ""}`}
                            {...register("user_id", { required: true })}
                        />
                        {errors.user_id && (
                            <p className="validation-error">
                                {errors.user_id.message}
                            </p>
                        )}
                    </div>
                    <div className="input-form">
                        <input
                            type="password"
                            placeholder="비밀번호"
                            className={`input ${
                                errors.password ? "error" : ""
                            }`}
                            {...register("password", { required: true })}
                        />
                        {errors.password && (
                            <p className="validation-error">
                                {errors.password.message}
                            </p>
                        )}
                    </div>
                    <div className="input-form input-has-gutter">
                        <input
                            type="password"
                            placeholder="비밀번호 재확인"
                            className={`input ${
                                errors.confirmPassword ? "error" : ""
                            }`}
                            {...register("confirmPassword", { required: true })}
                        />
                        {errors.confirmPassword && (
                            <p className="validation-error">
                                {errors.confirmPassword.message}
                            </p>
                        )}
                        <p className="validation-help-txt">
                            영문, 숫자, 특수문자 포함 8~12자리
                        </p>
                    </div>
                    <div className="input-form">
                        <input
                            type="text"
                            placeholder="이름"
                            className={`input ${errors.name ? "error" : ""}`}
                            {...register("name")}
                        />
                        {errors.name && (
                            <p className="validation-error">
                                {errors.name.message}
                            </p>
                        )}
                    </div>
                    <div className="input-form">
                        <div className="input-has-button">
                            <input
                                type="tel"
                                inputMode="numeric"
                                placeholder="휴대폰 번호"
                                className={`input ${errors.hp ? "error" : ""}`}
                                {...register("hp", { required: true })}
                                onInput={(e) => {
                                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                                }}
                            />
                            <button
                                type="button"
                                className={`btn btn-fit ${
                                    phone === "" || errors.hp 
                                        ? "btn-disabled"
                                        : "btn-active"
                                }`}
                                onClick={handleSendVerification}
                                // disabled={isSendCode}
                            >
                                {isSendCode ? "재발송" : "인증번호"}
                            </button>
                        </div>
                        {errors.hp && (
                            <p className="validation-error">
                                {errors.hp.message}
                            </p>
                        )}
                    </div>
                    <div
                        className="input-form input-has-gutter"
                        style={{ display: isSendCode ? "block" : "none" }}
                    >
                        <div>
                            <input
                                className={`input input-has-icon ${
                                    isCodeConfirm ? "complete" : ""
                                }`}
                                type="text"
                                name="verificationCode"
                                placeholder="인증 번호 입력"
                                maxLength={6}
                                {...register("verificationCode", {
                                    required: true,
                                    maxLength: 6,
                                })}
                                disabled={isCodeConfirm}
                            />
                        </div>
                    </div>
                    <p className="optional-guide-text">
                        원활한 잠수풀 예약과 안전한 이용을 위해 정보를
                        입력해 주세요.
                    </p>
                    <div className="input-form">
                        <input
                            type="text"
                            placeholder="생년월일(YYYY-MM-DD) (선택)"
                            className={`input ${
                                errors.birthDate ? "error" : ""
                            }`}
                            maxLength={10}
                            {...register("birthDate")}
                        />
                        {errors.birthDate && (
                            <p className="validation-error">
                                {errors.birthDate.message}
                            </p>
                        )}
                    </div>
                    <div className="radio-input-wrap input-has-gutter">
                        <label className="radio-name">
                            성별 <span className="optional-label">(선택)</span>
                        </label>
                        <div className="radio-group">
                            <input
                                type="radio"
                                id="genderM"
                                value="M"
                                {...register("gender")}
                                className="custom-radio-input"
                            />
                            <label
                                htmlFor="genderM"
                                className={`custom-radio-label ${
                                    gender === "M" ? "active" : ""
                                }`}
                            >
                                남
                            </label>
                            <input
                                type="radio"
                                id="genderF"
                                value="F"
                                {...register("gender")}
                                className="custom-radio-input"
                            />
                            <label
                                htmlFor="genderF"
                                className={`custom-radio-label ${
                                    gender === "F" ? "active" : ""
                                }`}
                            >
                                여
                            </label>
                        </div>
                    </div>

                    {/* 동의 */}
                    <div className="checkbox-input-wrap">
                        <input
                            type="checkbox"
                            className="checkbox-input"
                            name="allAgree"
                            checked={policyAgree.allAgree}
                            onChange={onChangePolicy}
                        />
                        <label className="agree-field">전체 동의</label>
                    </div>
                    <div className="agree-note">
                        모든 약관동의는 필수 및 선택정보에 대한 동의도 포함되어
                        있으며, 선택항목에 동의를 거부하시는 경우에도 서비스는
                        이용 가능합니다. 약관 전문은 아래 링크를 클릭하여 확인할
                        수 있습니다.
                    </div>
                    <div className="checkbox-input-wrap">
                        <input
                            type="checkbox"
                            className="checkbox-input"
                            name="ageConfirmed"
                            checked={policyAgree.ageConfirmed}
                            onChange={onChangePolicy}
                        />
                        <label className="agree-field">
                            본인은 만 14세 이상입니다.
                        </label>
                        {errors.ageConfirmed && (
                            <p className="validation-error">
                                {errors.ageConfirmed.message}
                            </p>
                        )}
                    </div>
                    <div className="checkbox-input-wrap">
                        <input
                            type="checkbox"
                            className="checkbox-input"
                            name="termsAccepted"
                            checked={policyAgree.termsAccepted}
                            onChange={onChangePolicy}
                        />
                        <label
                            className="agree-field"
                            onClick={() => navigate("/policy/terms")}
                            style={{
                                textDecoration: "underline",
                                cursor: "pointer",
                            }}
                        >
                            이용약관 동의(필수)
                        </label>
                        {errors.termsAccepted && (
                            <p className="validation-error">
                                {errors.termsAccepted.message}
                            </p>
                        )}
                    </div>
                    <div className="checkbox-input-wrap input-has-gutter">
                        <input
                            type="checkbox"
                            className="checkbox-input"
                            name="privacyAccepted"
                            checked={policyAgree.privacyAccepted}
                            onChange={onChangePolicy}
                        />
                        <label
                            className="agree-field"
                            onClick={() => navigate("/policy/privacy")}
                            style={{
                                textDecoration: "underline",
                                cursor: "pointer",
                            }}
                        >
                            개인정보 수집 및 이용동의(필수)
                        </label>
                        {errors.privacyAccepted && (
                            <p className="validation-error">
                                {errors.privacyAccepted.message}
                            </p>
                        )}
                    </div>
                    <Blank height={50} />
                    <div className="btn-wrap btn-bottom-fixed">
                        <button
                            type="submit"
                            className={`signup-button btn btn-large ${
                                activeSignupButton()
                                    ? "btn-active"
                                    : "btn-disabled"
                            }`}
                            disabled={!activeSignupButton()}
                        >
                            가입하기
                        </button>
                    </div>
                </form>
            </div>

            <CommonModal
                isOpen={isSuccessModalOpen}
                onRequestClose={closeSuccessModal}
                title="회원가입 성공!"
                className="signup-success-modal success-modal"
            >
                <>
                    <div>
                        <p>BUCCL의 회원이 되신 것을 환영합니다.</p>
                        <p>이제 다양한 서비스를 이용하실 수 있습니다.</p>
                    </div>
                    <div className="btn-wrap">
                        <button
                            onClick={closeSuccessModal}
                            className="btn btn-login"
                        >
                            로그인하러 가기
                        </button>
                    </div>
                </>
            </CommonModal>
            <CommonModal
                isOpen={isErrorMsg !== null}
                onRequestClose={() => setIsErrorMsg(null)}
                title={isErrorMsg}
                className="signup-error-modal error-modal"
            >
                <div className="btn-wrap">
                    <button
                        onClick={() => navigate("/find-id", { replace: true })}
                        className="btn btn-purple500"
                    >
                        아이디/비밀번호 찾기
                    </button>
                </div>
            </CommonModal>
        </>
    );
};

export default SignupPage;
